import { pluralize } from '@givebutter/util';
import { faCircleCheck, faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DeleteImportFormValues } from 'assets/js/pages/account-settings/imports/types';
import * as Form from 'components/form';
import { useDeletionStats } from 'customHooks/useDeletionStats';
import { Import } from 'types/domain';

interface DeleteContactStatsProps {
    importRecord: Import;
}

const DeleteContactStats = ({ importRecord }: DeleteContactStatsProps) => {
    const methods = Form.useGivebutterFormContext<DeleteImportFormValues>();
    const { path, control } = methods;

    const { deletionStats, hasContacts, hasTransactions, hasIgnoredContacts } =
        useDeletionStats(importRecord);

    const getLabel = (count: number) =>
        importRecord.resource === 'companies'
            ? pluralize(count, 'company', 'ies')
            : pluralize(count, 'contact');

    return (
        <>
            {hasContacts && (
                <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faCircleCheck} className="text-red-500" />
                    <div className="font-medium text-gray-700">
                        {deletionStats.contacts} {getLabel(deletionStats.contacts)} will be deleted
                    </div>
                </div>
            )}

            {hasTransactions && (
                <div className="flex flex-col">
                    <div className="flex gap-2">
                        <Form.UseFormCheckboxInput
                            control={control}
                            name={path('includeTransactions')}
                            className="mt-1"
                        />
                        <div>
                            <Form.Label name={path('includeTransactions')}>
                                Remove associated transactions
                            </Form.Label>

                            <div className="text-gray-600">
                                {deletionStats.transactions}{' '}
                                {pluralize(deletionStats.transactions, 'transaction')}{' '}
                                {deletionStats.transactions === 1 ? 'is' : 'are'} associated with
                                these imported contacts and can be deleted.
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {hasIgnoredContacts && (
                <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faCircleExclamation} className="text-gray-500" />
                        <div className="font-medium text-gray-700">
                            {deletionStats.ignored_contacts}{' '}
                            {getLabel(deletionStats.ignored_contacts)} cannot be deleted
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-4.5">&nbsp;</span>
                        <div className="text-gray-600">
                            {importRecord.resource === 'companies' ? 'Companies' : 'Contacts'} with
                            transactions processed through Givebutter cannot be deleted.
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export { DeleteContactStats };
